<template>
  <div class="p-0">
    <!-- Loading / Error -->
    <div v-if="loading" class="py-10">
      <Loading />
    </div>

    <div v-else-if="error" class="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
      {{ error }}
    </div>

    <div v-else>
      <!-- Header -->
      <div class="flex items-start justify-between gap-4 mb-4">
        <div>
          <h1 class="text-2xl font-bold text-gray-800">{{ task?.title || 'Detalle de Tarea' }}</h1>
          <h3 class="mt-2 text-1xl text-gray-800">Asignada a : {{ task?.responsible_user_name || '' }}</h3>
          <h3 class="mt-2 text-1xl text-gray-800">Tipo de Tarea: {{ task?.category_name || '' }}</h3>          
          <div class="mt-2 flex flex-wrap gap-2 items-center">
            <span class="px-2 py-1 text-xs font-semibold rounded-full" :class="statusClass(task?.status)">
              {{ statusLabel(task?.status) }}
            </span>
            <span class="px-2 py-1 text-xs font-semibold rounded-full" :class="priorityClass(task?.priority)">
              {{ priorityLabel(task?.priority) }}
            </span>
            <span v-if="task?.category?.name" class="text-xs text-gray-500">
              Categoría: {{ task.category.name }}
            </span>
          </div>
        </div>

        <div class="flex flex-col sm:flex-row gap-2 sm:justify-end">
          <button
            v-if="!editMode && canEditTask && !isCompleted"
            @click="editMode = true"
            class="px-4 py-2 text-sm font-medium bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
          >
            Editar
          </button>

          <button
            v-if="editMode"
            @click="submitEdit"
            :disabled="saving"
            class="px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-60"
          >
            {{ saving ? 'Guardando...' : 'Guardar' }}
          </button>


          <button
            v-if="!editMode && task?.status !== 'completada'"
            @click="changeStatus('en_progreso')"
            class="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Cambiar a - En Progreso
          </button>

          <button
            v-if="!editMode && task?.status !== 'completada'"
            @click="changeStatus('revisar')"
            class="px-4 py-2 text-sm font-medium bg-gray-600 text-white rounded-md hover:bg-gray-700"
          >
            Cambiar a - Para revisar
          </button>

          <button
            v-if="!editMode && task?.status !== 'completada'"
            @click="changeStatus('completada')"
            :disabled="!isOwner"
            class="px-4 py-2 text-sm font-medium bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            Cambiar a - Completada
          </button>

          <button
            v-if="!editMode && task?.status === 'completada' && canEditTask"
            @click="reopenTask"
            class="px-4 py-2 text-sm font-medium bg-yellow-600 text-white rounded-md hover:bg-yellow-700"
          >
            Reabrir
          </button>

          <!-- <button
            v-if="!editMode && task?.status !== 'cancelada' && task?.status !== 'completada'"
            @click="cancelTask"
            class="px-4 py-2 text-sm font-medium bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Cancelar
          </button> -->

           <button
            v-if="!editMode && task?.status !== 'cancelada' && task?.status !== 'completada'"
            @click="goToBack"
            class="px-4 py-2 text-sm font-medium bg-blue-600  text-white rounded-md hover:bg-blue-700"
          >
            Volver
          </button>
        </div>
      </div>

      <!-- Body -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div class="lg:col-span-2 space-y-4">
          <!-- Edit mode -->
          <div v-if="editMode" class="bg-white border border-gray-200 rounded-lg p-4">
            <h2 class="text-sm font-semibold text-gray-800 mb-3">Editar tarea</h2>

            <div class="space-y-3">
              <div>
                <label class="block text-xs font-medium text-gray-500 mb-1">Título</label>
                <input
                  v-model="editForm.title"
                  type="text"
                  class="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-indigo-600"
                />
              </div>

              <div>
                <label class="block text-xs font-medium text-gray-500 mb-1">Descripción</label>
                <textarea
                  v-model="editForm.description"
                  rows="4"
                  class="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-indigo-600"
                />
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label class="block text-xs font-medium text-gray-500 mb-1">Categoría</label>
                  <select
                    v-model="editForm.category_id"
                    class="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-indigo-600"
                  >
                    <option value="">Sin categoría</option>
                    <option v-for="c in categories" :key="c.id" :value="c.id">
                      {{ c.name }}
                    </option>
                  </select>
                </div>

                <div>
                  <label class="block text-xs font-medium text-gray-500 mb-1">Prioridad</label>
                  <select
                    v-model="editForm.priority"
                    class="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-indigo-600"
                  >
                    <option value="baja">Baja</option>
                    <option value="media">Media</option>
                    <option value="alta">Alta</option>
                    <option value="urgente">Urgente</option>
                  </select>
                </div>
              </div>

              <div>
                <label class="block text-xs font-medium text-gray-500 mb-1">Fecha límite</label>
                <input
                  v-model="editForm.due_at"
                  type="datetime-local"
                  class="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-indigo-600"
                />
              </div>
            </div>
          </div>

          <!-- Read mode -->
          <div v-else class="bg-white border border-gray-200 rounded-lg p-4">
            <h2 class="text-sm font-semibold text-gray-800 mb-3">Descripción</h2>
            <p class="text-sm text-gray-700 whitespace-pre-wrap">
              {{ task?.description || '-' }}
            </p>

            <div class="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div class="bg-gray-50 border border-gray-200 rounded-md p-3">
                <div class="text-xs font-semibold text-gray-500 uppercase mb-1">Fecha límite</div>
                <div class="font-medium text-gray-900">{{ formatDate(task?.due_at) }}</div>
              </div>
              <div class="bg-gray-50 border border-gray-200 rounded-md p-3">
                <div class="text-xs font-semibold text-gray-500 uppercase mb-1">Creada</div>
                <div class="font-medium text-gray-900">{{ formatDate(task?.created_at) }}</div>
              </div>
            </div>
          </div>

          <!-- Comments -->
          <div class="bg-white border border-gray-200 rounded-lg p-4">
            <div class="flex items-center justify-between mb-3">
              <h2 class="text-sm font-semibold text-gray-800">Comentarios</h2>
            </div>

            <div v-if="comments.length === 0" class="text-sm text-gray-500 mb-3">
              No hay comentarios.
            </div>

            <div v-else class="space-y-3 mb-4">
              <div
                v-for="c in comments"
                :key="c.id || c.comment_id"
                class="border border-gray-100 bg-gray-50 rounded-md p-3"
              >
                <div class="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5 text-xs text-gray-600">
                  <span>{{ c.user_name || c.created_by_name || c.user?.name || '—' }}</span>
                  <span class="text-gray-500 shrink-0 tabular-nums">{{
                    formatDateTime(c.created_at || c.createdAt)
                  }}</span>
                </div>
                <p class="text-sm text-gray-800 whitespace-pre-wrap">
                  {{ c.comment || c.text || '-' }}
                </p>
              </div>
            </div>

            <div v-if="!isCompleted" class="border-t border-gray-100 pt-3">
              <label class="block text-xs font-medium text-gray-500 mb-1">Agregar comentario</label>
              <textarea
                v-model="newComment"
                rows="3"
                class="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white focus:outline-none focus:border-indigo-600"
              />
              <div class="mt-3 flex justify-end">
                <button
                  @click="addComment"
                  :disabled="commentSaving || !newComment.trim()"
                  class="px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-60"
                >
                  {{ commentSaving ? 'Enviando...' : 'Enviar' }}
                </button>
              </div>
            </div>
            <p v-else class="border-t border-gray-100 pt-3 text-sm text-gray-500">
              Esta tarea está completada; no se pueden agregar comentarios.
            </p>
          </div>
        </div>

        <!-- Right column -->
        <div class="space-y-4">
          <!-- Attachments -->
          <div class="bg-white border border-gray-200 rounded-lg p-4">
            <h2 class="text-sm font-semibold text-gray-800 mb-3">Archivos adjuntos</h2>

            <div v-if="attachments.length === 0" class="text-sm text-gray-500">
              No hay adjuntos.
            </div>

            <div v-else class="space-y-2">
              <div
                v-for="a in attachments"
                :key="a.id || a.attachment_id"
                class="flex items-center justify-between gap-2 border border-gray-100 bg-gray-50 rounded-md p-3"
              >
                <div class="min-w-0">
                  <div class="text-sm font-medium text-gray-900 truncate">
                    {{ a.file_name || a.name || '-' }}
                  </div>
                  <div v-if="a.mime_type" class="text-xs text-gray-500 truncate">
                    {{ a.mime_type }}
                  </div>
                  <div v-if="attachmentUploadedAt(a)" class="text-xs text-gray-500">
                    Subido: {{ formatDateTime(attachmentUploadedAt(a)) }}
                  </div>
                </div>

                <div class="flex items-center gap-2">
                  <button
                    v-if="a.id || a.attachment_id"
                    type="button"
                    @click="downloadAttachment(a)"
                    :disabled="attachmentDownloadingId === (a.id || a.attachment_id)"
                    class="text-blue-600 hover:text-blue-800 disabled:opacity-50"
                    title="Descargar"
                  >
                    <i class="fa-solid fa-download"></i>
                  </button>

                  <button
                    v-if="!isCompleted"
                    @click="deleteAttachment(a.id || a.attachment_id)"
                    class="text-red-600 hover:text-red-800"
                    title="Eliminar"
                    :disabled="attachmentDeletingId === (a.id || a.attachment_id)"
                  >
                    <i class="fa-solid fa-trash-can"></i>
                  </button>
                </div>
              </div>
            </div>

            <div v-if="!isCompleted" class="mt-4 border-t border-gray-100 pt-4">
              <h3 class="text-sm font-semibold text-gray-800 mb-2">Agregar adjunto</h3>
              <div class="text-xs text-gray-500 mb-3">
                El archivo se sube al servidor (máx. 32 MiB según la API).
              </div>

              <div>
                <label class="block text-xs font-medium text-gray-500 mb-1">Archivo</label>
                <input
                  ref="attachmentFileInput"
                  type="file"
                  class="block w-full text-sm text-gray-700 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                  @change="onAttachmentFileSelected"
                />
                <p v-if="pendingAttachmentFile" class="mt-1 text-xs text-gray-600 truncate">
                  Seleccionado: {{ pendingAttachmentFile.name }}
                  <span v-if="pendingAttachmentFile.size" class="text-gray-500">
                    ({{ formatFileSize(pendingAttachmentFile.size) }})
                  </span>
                </p>
              </div>

              <div class="mt-3 flex justify-end">
                <button
                  @click="addAttachment"
                  :disabled="attachmentSaving || !pendingAttachmentFile"
                  class="px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-60"
                >
                  {{ attachmentSaving ? 'Subiendo...' : 'Subir adjunto' }}
                </button>
              </div>
            </div>
            <p v-else class="mt-4 border-t border-gray-100 pt-4 text-sm text-gray-500">
              Esta tarea está completada; no se pueden subir ni eliminar archivos.
            </p>
          </div>

          <!-- History -->
          <div class="bg-white border border-gray-200 rounded-lg p-4">
            <h2 class="text-sm font-semibold text-gray-800 mb-3">Historial</h2>

            <div v-if="history.length === 0" class="text-sm text-gray-500">
              No hay historial.
            </div>

            <div v-else class="space-y-2">
              <div
                v-for="h in history"
                :key="h.id || h.event_id || h.created_at"
                class="border border-gray-100 bg-gray-50 rounded-md p-3"
              >
                <div class="text-xs text-gray-600">
                  {{ formatDate(h.created_at) }}
                </div>
                <div class="text-sm text-gray-800">
                  {{ h.description || h.message || h.title || '-' }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>

import { mapGetters } from 'vuex'


export default {
  name: 'TaskDetailView',
  data() {
    return {
      loading: false,
      error: null,
      task: null,
      editMode: false,
      saving: false,
      categories: [],
      editForm: {
        title: '',
        description: '',
        category_id: '',
        priority: 'media',
        due_at: '',
      },

      comments: [],
      newComment: '',
      commentSaving: false,

      attachments: [],
      attachmentDeletingId: null,
      attachmentDownloadingId: null,

      history: [],

      pendingAttachmentFile: null,
      attachmentSaving: false,
    };
  },
  computed: {
    ...mapGetters('authentication', ['currentUser']),
    currentUserId() {
      return (
        this.$store.state.authentication?.user?.user_id ||
        this.$store.state.authentication?.user?.id ||
        ''
      );
    },
    taskCreatorId() {
      return (
        this.task?.created_by_user_id ||
        this.task?.attributes?.created_by_user_id ||
        this.task?.createdByUserId ||
        ''
      );
    },
    canEditTask() {
      if (
        this.taskCreatorId == null ||
        this.taskCreatorId === '' ||
        this.currentUserId == null ||
        this.currentUserId === ''
      ) {
        return false;
      }
      return String(this.taskCreatorId) === String(this.currentUserId);
    },
    isCompleted() {
      return (this.task?.status || '').toLowerCase() === 'completada';
    },
    isOwner() {
      return this.task?.created_by_user_id == this.currentUserId
    }
  },
  created() {
    this.editMode = false;
    this.loadAll();
  },
  methods: {
    unwrapData(resp) {
      return resp?.data?.data ?? resp?.data?.data?.data ?? resp?.data ?? [];
    },
    /**
     * GET /tasks/:id puede devolver la tarea plana en `data`, o anidada en
     * `data.task` / `task`, o estilo JSON:API con `attributes`.
     */
    normalizeTaskFromResponse(resp) {
      let raw = resp?.data?.data ?? resp?.data?.data?.data ?? resp?.data ?? null;
      if (raw == null) return null;
      if (Array.isArray(raw)) raw = raw[0] ?? null;
      if (raw == null || typeof raw !== 'object') return null;

      if (raw.task != null && typeof raw.task === 'object') raw = raw.task;
      else if (raw.item != null && typeof raw.item === 'object') raw = raw.item;

      const attrs =
        raw.attributes != null && typeof raw.attributes === 'object' ? raw.attributes : null;
      const t = attrs ? { ...raw, ...attrs, id: raw.id ?? attrs.id } : { ...raw };

      return {
        ...t,
        title: t.title ?? t.name ?? t.subject,
        description: t.description ?? t.body ?? t.descripcion ?? t.details,
        due_at: t.due_at ?? t.due_date ?? t.deadline ?? t.fecha_limite ?? t.ends_at,
        created_at: t.created_at ?? t.createdAt ?? t.created ?? t.fecha_creacion,
        category: t.category ?? t.task_category,
      };
    },
    async loadAll() {
      this.loading = true;
      this.error = null;

      const id = this.$route.params.id;
      try {
        await this.fetchCategories();

        const [taskRes, commentsRes, attachmentsRes, historyRes] = await Promise.all([
          this.$http.get(`/tasks/${id}`),
          this.$http.get(`/tasks/${id}/comments`),
          this.$http.get(`/tasks/${id}/attachments`),
          this.$http.get(`/tasks/${id}/history`),
        ]);

        this.task = this.normalizeTaskFromResponse(taskRes);
        const completed = (this.task?.status || '').toLowerCase() === 'completada';
        this.editMode =
          !completed && this.$route.query.edit === '1' && this.canEditTask;
        this.comments = this.normalizeArray(this.unwrapData(commentsRes));
        this.attachments = this.normalizeArray(this.unwrapData(attachmentsRes));
        this.history = this.normalizeArray(this.unwrapData(historyRes));

        this.initEditForm();
      } catch (err) {
        this.error =
          err?.response?.data?.message ||
          err?.response?.data?.data?.message ||
          err?.message ||
          'Error al cargar el detalle de la tarea';
      } finally {
        this.loading = false;
      }
    },
    normalizeArray(value) {
      if (Array.isArray(value)) return value;
      if (value?.data && Array.isArray(value.data)) return value.data;
      return [];
    },
    async fetchCategories() {
      const res = await this.$http.get('/task-categories', { params: { active_only: true } });
      const payload = this.unwrapData(res);
      this.categories = Array.isArray(payload) ? payload : payload?.data || [];
    },
    initEditForm() {
      const t = this.task || {};
      if (!t) return;
      this.editForm = {
        title: t.title || '',
        description: t.description || '',
        category_id: t.category_id || t.category?.id || '',
        priority: t.priority || 'media',
        due_at: this.toDatetimeLocalValue(t.due_at),
      };
    },
    toDatetimeLocalValue(value) {
      if (!value) return '';
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return '';
      // datetime-local expects "YYYY-MM-DDTHH:mm"
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    toISO(value) {
      if (!value) return null;
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return null;
      return d.toISOString();
    },
    statusLabel(status) {
      const s = (status || '').toLowerCase();
      const map = {
        pendiente: 'Pendiente',
        en_progreso: 'En progreso',
        bloqueada: 'Bloqueada',
        completada: 'Completada',
        cancelada: 'Cancelada',
        vencida: 'Vencida',
      };
      return map[s] || status || '-';
    },
    statusClass(status) {
      const s = (status || '').toLowerCase();
      const map = {
        pendiente: 'bg-yellow-100 text-yellow-900 border-yellow-200',
        en_progreso: 'bg-blue-100 text-blue-900 border-blue-200',
        bloqueada: 'bg-purple-100 text-purple-900 border-purple-200',
        completada: 'bg-green-100 text-green-900 border-green-200',
        vencida: 'bg-red-100 text-red-900 border-red-200',
        cancelada: 'bg-gray-100 text-gray-800 border-gray-200',
      };
      return map[s] || 'bg-gray-100 text-gray-800 border-gray-200';
    },
    priorityLabel(priority) {
      const p = (priority || '').toLowerCase();
      const map = { baja: 'Baja', media: 'Media', alta: 'Alta', urgente: 'Urgente' };
      return map[p] || priority || '-';
    },
    priorityClass(priority) {
      const p = (priority || '').toLowerCase();
      const map = {
        baja: 'bg-blue-50 text-blue-700 border-blue-100',
        media: 'bg-indigo-50 text-indigo-700 border-indigo-100',
        alta: 'bg-orange-50 text-orange-700 border-orange-100',
        urgente: 'bg-red-50 text-red-700 border-red-100',
      };
      return map[p] || 'bg-gray-100 text-gray-800 border-gray-200';
    },
    formatDate(dateString) {
      if (!dateString) return '-';
      const d = new Date(dateString);
      if (Number.isNaN(d.getTime())) return String(dateString);
      return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },
    formatDateTime(dateString) {
      if (!dateString) return '-';
      const d = new Date(dateString);
      if (Number.isNaN(d.getTime())) return String(dateString);
      return d.toLocaleString('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    },
    formatFileSize(bytes) {
      if (bytes == null || Number.isNaN(bytes)) return '';
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    },
    attachmentUploadedAt(a) {
      if (!a) return null;
      return (
        a.created_at ||
        a.createdAt ||
        a.uploaded_at ||
        a.uploadedAt ||
        a.fecha_subida ||
        a.fecha_creacion ||
        null
      );
    },
    onAttachmentFileSelected(event) {
      const f = event.target.files?.[0];
      this.pendingAttachmentFile = f || null;
    },
    async changeStatus(newStatus) {
      const id = this.$route.params.id;
      this.loading = true;
      try {
        await this.$http.put(`/tasks/${id}/status`, { status: newStatus });
        await this.loadAll();
      } catch (err) {
        this.error = err?.response?.data?.message || err?.message || 'Error al cambiar el estado';
      } finally {
        this.loading = false;
      }
    },

    goToBack() {
      this.$router.push({name: 'TasksView'})
    },
    async cancelTask() {
      const id = this.$route.params.id;
      this.loading = true;
      try {
        await this.$http.put(`/tasks/${id}/cancel`);
        await this.loadAll();
      } catch (err) {
        this.error = err?.response?.data?.message || err?.message || 'Error al cancelar la tarea';
      } finally {
        this.loading = false;
      }
    },
    async reopenTask() {
      if (!this.canEditTask) {
        this.error = 'Solo quien creó la tarea puede reabrirla.';
        return;
      }
      const id = this.$route.params.id;
      this.loading = true;
      try {
        await this.$http.put(`/tasks/${id}/reopen`);
        await this.loadAll();
      } catch (err) {
        this.error = err?.response?.data?.message || err?.message || 'Error al reabrir la tarea';
      } finally {
        this.loading = false;
      }
    },
    async submitEdit() {
      if (this.isCompleted) {
        this.error = 'No se puede editar una tarea completada. Reabrila si necesitás cambios.';
        return;
      }
      if (!this.canEditTask) {
        this.error = 'Solo quien creó la tarea puede editarla.';
        return;
      }
      const id = this.$route.params.id;
      this.saving = true;
      this.error = null;
      try {
        const dueAt = this.toISO(this.editForm.due_at);
        await this.$http.put(`/tasks/${id}`, {
          title: this.editForm.title,
          description: this.editForm.description,
          category_id: this.editForm.category_id || null,
          priority: this.editForm.priority,
          due_at: dueAt,
        });
        this.editMode = false;
        await this.loadAll();
      } catch (err) {
        this.error = err?.response?.data?.message || err?.message || 'Error al guardar la tarea';
      } finally {
        this.saving = false;
      }
    },
    async addComment() {
      if (this.isCompleted) {
        this.error = 'No se pueden agregar comentarios en una tarea completada.';
        return;
      }
      const id = this.$route.params.id;
      const comment = this.newComment.trim();
      if (!comment) return;
      this.commentSaving = true;
      this.error = null;
      try {
        await this.$http.post(`/tasks/${id}/comments`, { comment });
        this.newComment = '';
        const res = await this.$http.get(`/tasks/${id}/comments`);
        this.comments = this.normalizeArray(this.unwrapData(res));
      } catch (err) {
        this.error = err?.response?.data?.message || err?.message || 'Error al enviar comentario';
      } finally {
        this.commentSaving = false;
      }
    },
    async downloadAttachment(attachment) {
      const taskId = this.$route.params.id;
      const attachmentId = attachment?.id || attachment?.attachment_id;
      if (!attachmentId) return;

      this.attachmentDownloadingId = attachmentId;
      this.error = null;
      try {
        const response = await this.$http.get(`/tasks/${taskId}/attachments/${attachmentId}/file`, {
          responseType: 'blob',
        });
        const blob = response.data;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const cd = response.headers?.['content-disposition'];
        const filenameMatch = cd?.match(/filename\*?=['"]?(?:UTF-8'')?([^'";\n]+)['"]?/i);
        const defaultName = attachment.file_name || attachment.name || 'adjunto';
        link.setAttribute('download', filenameMatch ? filenameMatch[1].trim() : defaultName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        let msg = 'Error al descargar el adjunto.';
        if (err.response?.data instanceof Blob) {
          try {
            const text = await err.response.data.text();
            const parsed = JSON.parse(text);
            if (parsed.message) msg = parsed.message;
          } catch (_) {
            /* ignore */
          }
        } else if (err.response?.data?.message) {
          msg = err.response.data.message;
        } else if (err.message) {
          msg = err.message;
        }
        this.error = msg;
      } finally {
        this.attachmentDownloadingId = null;
      }
    },

    async deleteAttachment(attachmentId) {
      if (this.isCompleted) {
        this.error = 'No se pueden eliminar adjuntos en una tarea completada.';
        return;
      }
      if (!attachmentId) return;
      const id = this.$route.params.id;
      this.attachmentDeletingId = attachmentId;
      this.error = null;
      try {
        await this.$http.delete(`/tasks/${id}/attachments/${attachmentId}`);
        const res = await this.$http.get(`/tasks/${id}/attachments`);
        this.attachments = this.normalizeArray(this.unwrapData(res));
      } catch (err) {
        this.error = err?.response?.data?.message || err?.message || 'Error al eliminar adjunto';
      } finally {
        this.attachmentDeletingId = null;
      }
    },

    async addAttachment() {
      if (this.isCompleted) {
        this.error = 'No se pueden subir adjuntos en una tarea completada.';
        return;
      }
      const id = this.$route.params.id;
      const file = this.pendingAttachmentFile;
      if (!file) return;

      const formData = new FormData();
      formData.append('file', file);

      this.attachmentSaving = true;
      this.error = null;
      try {
        await this.$http.post(`/tasks/${id}/attachments`, formData);

        const res = await this.$http.get(`/tasks/${id}/attachments`);
        this.attachments = this.normalizeArray(this.unwrapData(res));

        this.pendingAttachmentFile = null;
        const input = this.$refs.attachmentFileInput;
        if (input) input.value = '';
      } catch (err) {
        this.error = err?.response?.data?.message || err?.message || 'Error al subir el adjunto';
      } finally {
        this.attachmentSaving = false;
      }
    },
  },
};
</script>

<style scoped>
</style>

